import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid place-items-center px-6 py-12">
    <div className="w-full max-w-sm space-y-6 fade-up">
      <div className="flex flex-col items-center gap-1">
        <img src="/logo.png" alt="JP Hospitality" className="w-20 h-20" />
        <span className="text-2xl font-bold text-[#24474A]">TSKPRO</span>
        <span className="text-[11px] tracking-[0.3em] uppercase text-slate-500">Team - Task - Productivity</span>
      </div>
      <div>
        <h2 className="text-3xl font-bold text-[#24474A]">{title}</h2>
        <p className="text-slate-500 mt-2">{subtitle}</p>
      </div>
      {children}
      <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-[#24474A]" data-testid="back-to-login-link"><ArrowLeft className="w-4 h-4" /> Back to sign in</Link>
    </div>
  </div>
);

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(data.message);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Forgot password?" subtitle="Enter your work email and we'll send you a link to choose a new password.">
      {sent ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-3" data-testid="forgot-success">{sent}</div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div className="space-y-1.5"><Label>Work email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="forgot-email-input" /></div>
          {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="forgot-error">{error}</div>}
          <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="forgot-submit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}</Button>
        </form>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return setError("Password must be at least 6 characters");
    if (pw !== confirm) return setError("Passwords do not match");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Choose a new password" subtitle="Use at least 6 characters.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="reset-password-input" /></div>
        <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" data-testid="reset-confirm-input" /></div>
        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="reset-error">{error}</div>}
        <Button type="submit" className="w-full h-11 rounded-full" disabled={busy} data-testid="reset-submit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}</Button>
      </form>
    </Shell>
  );
};
